/** The tour-wide links (the master + custom links, U4's default view): a fixed grid of 36px
 *  rows with hairlines — Created · the Master link chip · Verification (resolved) ·
 *  Registered · the address tail · Copy, QR and ⋯. A revoked row is tinted, its identity
 *  struck through, and it reads "Revoked 5 Oct · Restore".
 *
 *  Keyboard: the list is ONE tab stop with roving focus — ↑/↓ (Home / End) between rows,
 *  ←/→ through a row's actions, Delete opens the revoke confirm, Enter (or Shift+F10)
 *  the row's ⋯ menu. Each row is named for what tells it apart ("Link created 5 Oct 2026,
 *  any date, 12 registered"). Nothing opens on hover; no state rests on colour alone.
 *
 *  Nothing here depends on the record's mode: the actions act at once (Draft-Or-Now), so E
 *  moves nothing (Zero-Re-Layout). */
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardOutlinedIcon from '@mui/icons-material/KeyboardOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { alpha, Box, Chip, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import type { TourSelfregLink } from '@/api/tours';
import { ImmediateActionButton } from '@/features/records/actions/ImmediateActionButton';
import { prefersReducedMotion } from '@/features/records/page/recordPage';

import {
  addressTail,
  createdWords,
  formatShortDate,
  linkRowName,
  REVOKED_NO_COPY,
  registeredWords,
  verificationSentence,
  verificationWords,
} from './linkWords';

/** A row's height (the console's compact grid row). */
export const ROW_PX = 36;
/** How long a new link's row stays tinted after Create. */
export const NEW_TINT_MS = 1500;

/** Created · Master · Verification · Registered · Address · actions. */
export const ROW_COLUMNS = 'minmax(64px, 0.9fr) 104px minmax(0, 1.4fr) minmax(0, 0.8fr) minmax(0, 1fr) auto';

export const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

/** The list's keys, said under it and given as its description. */
export const KEY_HINT = '↑ ↓ move between links · ← → reach a link’s actions · Enter opens its menu · Delete revokes';

type Part = 'row' | 'restore' | 'copy' | 'qr' | 'menu';

/** A row's focus stops, left to right. A revoked row has no Copy / QR (disabled). */
function partsOf(link: TourSelfregLink, canRestore: boolean): Part[] {
  if (!link.revoked_ts) return ['row', 'copy', 'qr', 'menu'];
  return canRestore ? ['row', 'restore', 'menu'] : ['row', 'menu'];
}

export interface LinkRowHandlers {
  onCopy: (link: TourSelfregLink) => void;
  onQr: (link: TourSelfregLink, anchor: HTMLElement) => void;
  onMenu: (link: TourSelfregLink, anchor: HTMLElement) => void;
  onRevoke: (link: TourSelfregLink, anchor: HTMLElement) => void;
  onRestore: (link: TourSelfregLink) => void;
}

export function TourWideList({
  links,
  handlers,
  canRestore,
  restoring = false,
  focusRequest,
  onFocusRequestDone,
}: {
  links: readonly TourSelfregLink[];
  handlers: LinkRowHandlers;
  canRestore: boolean;
  /** A restore is on its way: the Restore buttons wait. */
  restoring?: boolean;
  /** After Create: scroll to this link, focus its Copy, tint it for a moment. */
  focusRequest: string | null;
  onFocusRequestDone: () => void;
}) {
  const [focus, setFocus] = useState<{ id: string; part: Part } | null>(null);
  const [tinted, setTinted] = useState<string | null>(null);
  const hintId = useId();
  const els = useRef(new Map<string, HTMLElement | null>());
  const key = (id: string, part: Part) => `${id}:${part}`;
  const ref = (id: string, part: Part) => (el: HTMLElement | null) => {
    els.current.set(key(id, part), el);
  };

  // The one tab stop: the focused part while its row is listed, else the first row.
  const current =
    focus && links.some((l) => l.id === focus.id && partsOf(l, canRestore).includes(focus.part))
      ? focus
      : links[0]
        ? { id: links[0].id, part: 'row' as Part }
        : null;
  const tab = (id: string, part: Part) => (current?.id === id && current.part === part ? 0 : -1);
  const moveTo = (id: string, part: Part) => {
    setFocus({ id, part });
    els.current.get(key(id, part))?.focus();
  };

  // After Create: the new row once it is listed.
  useEffect(() => {
    if (!focusRequest || !links.some((l) => l.id === focusRequest)) return undefined;
    els.current.get(key(focusRequest, 'row'))?.scrollIntoView?.({ block: 'nearest' });
    moveTo(focusRequest, 'copy');
    onFocusRequestDone();
    if (!prefersReducedMotion()) setTinted(focusRequest);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest, links]);
  // The focused row left the list (revoked, Show revoked off): DOM focus fell to the page,
  // so hand it to the row that took its place.
  const focusIndex = useRef(0);
  useEffect(() => {
    if (!focus) return;
    const i = links.findIndex((l) => l.id === focus.id);
    if (i >= 0) {
      focusIndex.current = i;
      return;
    }
    const next = links[Math.min(focusIndex.current, links.length - 1)];
    if (next && (document.activeElement === document.body || document.activeElement === null)) moveTo(next.id, 'row');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [links, focus]);
  useEffect(() => {
    if (!tinted) return undefined;
    const t = window.setTimeout(() => setTinted(null), NEW_TINT_MS);
    return () => window.clearTimeout(t);
  }, [tinted]);

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (!current || e.altKey || e.metaKey || e.ctrlKey) return;
    const i = links.findIndex((l) => l.id === current.id);
    const link = links[i];
    if (!link) return;
    const parts = partsOf(link, canRestore);
    const at = parts.indexOf(current.part);
    const toRow = (j: number) => {
      const target = links[Math.max(0, Math.min(links.length - 1, j))]!;
      const tparts = partsOf(target, canRestore);
      // Keep the column when the row has it (Copy stays Copy), else the nearest part.
      const part = tparts.includes(current.part) ? current.part : tparts[Math.min(at, tparts.length - 1)]!;
      moveTo(target.id, part);
    };
    const menu = () => {
      const anchor = els.current.get(key(link.id, 'menu'));
      if (anchor) handlers.onMenu(link, anchor);
    };
    switch (e.key) {
      case 'ArrowDown':
        toRow(i + 1);
        break;
      case 'ArrowUp':
        toRow(i - 1);
        break;
      case 'Home':
        moveTo(links[0]!.id, 'row');
        break;
      case 'End':
        moveTo(links[links.length - 1]!.id, 'row');
        break;
      case 'ArrowRight':
        moveTo(link.id, parts[Math.min(parts.length - 1, at + 1)]!);
        break;
      case 'ArrowLeft':
        moveTo(link.id, parts[Math.max(0, at - 1)]!);
        break;
      case 'Delete': {
        if (link.revoked_ts) return;
        const anchor = els.current.get(key(link.id, 'menu'));
        if (anchor) handlers.onRevoke(link, anchor);
        break;
      }
      case 'Enter':
      case ' ':
        // On the row itself: its menu. On a button: the button's own action.
        if (current.part !== 'row') return;
        menu();
        break;
      case 'F10':
        if (!e.shiftKey) return;
        menu();
        break;
      case 'ContextMenu':
        menu();
        break;
      default:
        return;
    }
    e.preventDefault();
  };

  return (
    <>
    <Box
      role="grid"
      aria-label="Tour-wide self-registration links"
      aria-describedby={hintId}
      aria-rowcount={links.length}
      onKeyDown={onKeyDown}
      data-selfreg-list
      sx={{ borderBottom: 1, borderColor: 'divider' }}
    >
      {links.map((link, index) => {
        const revoked = Boolean(link.revoked_ts);
        const name = linkRowName(link);
        const created = createdWords(link.created_ts);
        const verify = verificationWords(link);
        const tail = addressTail(link);
        const focusOn = (part: Part) => () => setFocus({ id: link.id, part });
        return (
          <Box
            role="row"
            key={link.id}
            aria-label={name}
            aria-rowindex={index + 1}
            data-selfreg-link={link.id}
            data-revoked={revoked || undefined}
            data-master={link.is_master || undefined}
            data-new={tinted === link.id || undefined}
            sx={{
              display: 'grid',
              gridTemplateColumns: ROW_COLUMNS,
              alignItems: 'center',
              columnGap: 1,
              height: ROW_PX,
              px: 0.5,
              borderTop: 1,
              borderColor: 'divider',
              fontVariantNumeric: 'tabular-nums',
              bgcolor: (t) =>
                tinted === link.id ? alpha(t.palette.primary.main, 0.08) : revoked ? t.palette.action.hover : 'transparent',
              transition: 'background-color 300ms ease-out',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              // The row's own focus stop is its first cell; the ring goes round the whole row.
              '&:has([data-row-cell]:focus-visible)': { boxShadow: (t) => `inset 0 0 0 2px ${t.palette.primary.main}` },
            }}
          >
            <Tooltip title={created.tooltip} placement="top-start" describeChild>
              <Box
                role="gridcell"
                ref={ref(link.id, 'row')}
                tabIndex={tab(link.id, 'row')}
                aria-label={name}
                onFocus={(e) => {
                  if (e.target === e.currentTarget) setFocus({ id: link.id, part: 'row' });
                }}
                data-row-cell
                sx={{ minWidth: 0, outline: 'none' }}
              >
                <Typography
                  variant="body2"
                  noWrap
                  color={revoked ? 'text.secondary' : 'text.primary'}
                  sx={{ textDecoration: revoked ? 'line-through' : 'none' }}
                  data-cell="created"
                >
                  {created.text}
                </Typography>
              </Box>
            </Tooltip>
            <Box role="gridcell" sx={{ minWidth: 0, display: 'flex' }} data-cell="master">
              <KindSlot link={link} struck={revoked} />
            </Box>
            {revoked ? (
              <Box
                role="gridcell"
                sx={{ gridColumn: 'span 2', minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}
                data-cell="revoked"
              >
                <Typography variant="body2" color="text.secondary" noWrap>
                  Revoked {formatShortDate(link.revoked_ts)}
                  {canRestore ? ' ·' : ''}
                </Typography>
                {canRestore && (
                  <ImmediateActionButton
                    ref={ref(link.id, 'restore') as (el: HTMLButtonElement | null) => void}
                    tabIndex={tab(link.id, 'restore')}
                    onFocus={focusOn('restore')}
                    onClick={() => handlers.onRestore(link)}
                    disabled={restoring}
                    aria-label={`Restore link ${tail} now`}
                    sx={{ minWidth: 0, py: 0, flex: 'none' }}
                  >
                    Restore
                  </ImmediateActionButton>
                )}
              </Box>
            ) : (
              <>
                {/* Two short lines inside the 36px row, so neither part truncates at the
                    panel's width; the whole sentence is in the tooltip. */}
                <Tooltip title={verificationSentence(link)} placement="top-start" describeChild>
                  <Box role="gridcell" sx={{ minWidth: 0, display: 'grid' }} data-cell="verification">
                    <Typography variant="body2" noWrap sx={{ lineHeight: 1.15 }} data-verify-what>
                      {verify.text}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ lineHeight: 1.2 }} data-verify-source>
                      {verify.source}
                    </Typography>
                  </Box>
                </Tooltip>
                <Cell data="registered" muted={link.registered_count === 0}>
                  {registeredWords(link.registered_count)}
                </Cell>
              </>
            )}
            <Cell tooltip={link.link} struck={revoked} mono data="address">
              {tail}
            </Cell>
            <Box role="gridcell" sx={{ display: 'flex', alignItems: 'center' }} data-cell="actions">
              {/* A revoked link no longer works: no Copy / QR until it is restored (the span
                  keeps the reason's tooltip working over the disabled button). */}
              <Tooltip title={revoked ? REVOKED_NO_COPY : 'Copy link'} describeChild>
                <Box component="span" sx={{ display: 'inline-flex' }} data-disabled-reason={revoked || undefined}>
                  <IconButton
                    ref={ref(link.id, 'copy')}
                    size="small"
                    tabIndex={tab(link.id, 'copy')}
                    onFocus={focusOn('copy')}
                    onClick={() => handlers.onCopy(link)}
                    disabled={revoked}
                    aria-label={`Copy link ${tail}`}
                    data-action="copy"
                  >
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              </Tooltip>
              <Tooltip title={revoked ? REVOKED_NO_COPY : 'QR code'} describeChild>
                <Box component="span" sx={{ display: 'inline-flex' }} data-disabled-reason={revoked || undefined}>
                  <IconButton
                    ref={ref(link.id, 'qr')}
                    size="small"
                    tabIndex={tab(link.id, 'qr')}
                    onFocus={focusOn('qr')}
                    onClick={(e) => handlers.onQr(link, e.currentTarget)}
                    disabled={revoked}
                    aria-label={`QR code of link ${tail}`}
                    aria-haspopup="dialog"
                    data-action="qr"
                  >
                    <QrCode2Icon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Tooltip>
              <Tooltip title="More actions">
                <IconButton
                  ref={ref(link.id, 'menu')}
                  size="small"
                  tabIndex={tab(link.id, 'menu')}
                  onFocus={focusOn('menu')}
                  onClick={(e) => handlers.onMenu(link, e.currentTarget)}
                  aria-label={`Actions of link ${tail}`}
                  aria-haspopup="menu"
                  data-action="menu"
                >
                  <MoreHorizIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        );
      })}
    </Box>
    <Typography
      id={hintId}
      variant="caption"
      color="text.secondary"
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
      data-selfreg-keys
    >
      <KeyboardOutlinedIcon aria-hidden sx={{ fontSize: 14 }} />
      {KEY_HINT}
    </Typography>
    </>
  );
}

/** One text cell: one line, ellipsis, the whole text (or more) in its tooltip. */
function Cell({
  children,
  tooltip,
  struck = false,
  mono = false,
  muted = false,
  data,
}: {
  children: ReactNode;
  tooltip?: string;
  struck?: boolean;
  mono?: boolean;
  muted?: boolean;
  data: string;
}) {
  const text = (
    <Typography
      variant="body2"
      noWrap
      role="gridcell"
      color={struck || muted ? 'text.secondary' : 'text.primary'}
      sx={{
        minWidth: 0,
        textDecoration: struck ? 'line-through' : 'none',
        ...(mono ? { fontFamily: MONO, fontSize: '0.8125rem' } : {}),
      }}
      data-cell={data}
    >
      {children}
    </Typography>
  );
  return tooltip ? (
    // describeChild: the title describes the value; it must not replace its words as its name.
    <Tooltip title={tooltip} placement="top-start" describeChild>
      {text}
    </Tooltip>
  ) : (
    text
  );
}

/** The master's chip (notifications use it); a tagged undated link's tags (D3). */
function KindSlot({ link, struck }: { link: TourSelfregLink; struck: boolean }) {
  if (link.is_master) {
    return (
      <Tooltip title="Notifications use this link" describeChild>
        <Chip
          size="small"
          variant="outlined"
          icon={<MailOutlineIcon />}
          label="Master link"
          data-master-chip
          sx={{ height: 22, fontWeight: 600, '& .MuiChip-icon': { fontSize: 14 } }}
        />
      </Tooltip>
    );
  }
  if (link.tags.length === 0) return null;
  const names = link.tags.map((t) => t.name ?? `#${t.id}`);
  return (
    <Tooltip title={`Tags: ${names.join(', ')}`} describeChild>
      <Chip
        size="small"
        variant="outlined"
        label={names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0]}
        sx={{ height: 22, maxWidth: '100%', textDecoration: struck ? 'line-through' : 'none' }}
        data-tags-chip
      />
    </Tooltip>
  );
}

/** Loading: as many skeleton rows as the tour has links (at least the empty line's one), so
 *  the list does not move when it arrives. */
export function TourWideSkeleton({ rows }: { rows: number }) {
  return (
    <Box role="status" aria-busy="true" aria-label="Loading the tour-wide links" data-selfreg-skeleton sx={{ borderBottom: rows > 0 ? 1 : 0, borderColor: 'divider' }}>
      {Array.from({ length: Math.max(1, rows) }, (_, i) => (
        <Box
          key={i}
          sx={{
            height: ROW_PX,
            display: 'grid',
            gridTemplateColumns: ROW_COLUMNS,
            columnGap: 1,
            alignItems: 'center',
            px: 0.5,
            borderTop: rows > 0 ? 1 : 0,
            borderColor: 'divider',
          }}
        >
          <Skeleton variant="text" />
          <span />
          <Skeleton variant="text" />
          <Skeleton variant="text" />
          <Skeleton variant="text" />
          <Skeleton variant="text" width={84} />
        </Box>
      ))}
    </Box>
  );
}
